function clamp(num, min, max) {
  return Math.min(Math.max(num, min), max);
}

function callOnLoad(callback) {
  if (document.readyState === 'complete') {
    callback();
  } else {
    window.addEventListener('load', callback);
  }
}

function callOnDocReady(callback) {
  if (document.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', callback)
  } else {
    callback()
  }
}

function debounce(fn, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let wait = false;
  return (...args) => {
    if (wait) return;

    fn(...args);
    wait = true;

    setTimeout(() => {
      wait = false;
    }, delay);
  };
}

// TODO: Test with rest parameters
function once(fn) {
  let ran = false;
  let result;
  return function (...args) {
    if (ran) return result;
    result = fn.apply(this, args);
    ran = true;
    return result;
  };
}

// TODO: Test with rest parameters
function memoize(fn) {
  const cache = new Map();
  return function (...args) {
    const key = JSON.stringify(args);
    if (cache.has(key)) return cache.get(key);

    const result = fn.apply(this, args);
    cache.set(key, result);
    return result;
  };
}

function curry(fn, arity = fn.length) {
  return function curried(...args) {
    if (args.length >= arity) return fn(...args);
    return function (...moreArgs) {
      return curried(...args, ...moreArgs);
    };
  };
}

// TODO: Test using quokka
function partial(fn, ...args) {
  return (..._args) => fn(...args, ..._args);
}

function pipe(...fns) {
  return function piped(...args) {
    return fns.reduce((result, fn) => [fn.call(this, ...result)], args)[0];
  };
}

function compose(...fns) {
  return function composed(...args) {
    return fns.reduceRight((result, fn) => [fn.call(this, ...result)], args)[0];
  };
}

function pick(obj, keys) {
  return keys.reduce((acc, key) => {
    if (obj.hasOwnProperty(key)) {
      acc[key] = obj[key];
    }
    return acc;
  }, {});
}

function omit(obj, keys) {
  return Object.keys(obj)
    .filter(key => !keys.includes(key))
    .reduce((acc, key) => {
      acc[key] = obj[key];
      return acc;
    }, {});
}

function zip(...arrays) {
  const maxLength = Math.max(...arrays.map(array => array.length));
  return Array.from({ length: maxLength }).map((_, i) => {
    return Array.from({ length: arrays.length }, (_, j) => arrays[j][i]);
  });
}

function range(min, max, step = 1) {
  if (max === undefined) {
    max = min;
    min = step;
  }

  return Array.from({ length: Math.floor((max - min) / step) + 1 }, (_, i) => min + i * step);
}

// Reinject all script tags within an AJAX-loaded element to allow script execution. By default, scripts are disabled when using element.innerHTML.
function reinjectScripts(element) {
  console.debug('Reinjecting script tags for element:', element);

  element.querySelectorAll('script').forEach(oldScriptTag => {
    const newScriptTag = document.createElement('script');
    Array.from(oldScriptTag.attributes).forEach(attribute => {
      newScriptTag.setAttribute(attribute.name, attribute.value);
    });
    newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
    oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
  });
}

function camelToKebab(str) {
  if (str != str.toLowerCase()) {
    str = str.replace(/[A-Z]/g, m => '-' + m.toLowerCase());
  }

  return str;
}

function parseAttribute(value) {
  if (VALUE_PATTERNS.boolean.test(value)) {
    return value === 'true';
  }

  if (VALUE_PATTERNS.number.test(value)) {
    return Number(value);
  }

  return value;
}

function initRolloverImages() {
  // Swap data-src to src on hover media (image or video) on first mouseenter so the
  // media is fetched and ready when CSS opacity transition kicks in.
  // On mobile mouseenter never fires, so hover media is never fetched.
  // Videos are paused and reset on mouseleave.
  document.querySelectorAll('.card .media--hover-effect .card__media-item + .card__media-item').forEach(el => {
    const card = el.closest('.card');
    if (!card) return;

    function onMouseEnter() {
      if (window.innerWidth < 1024) return;

      if (el.tagName === 'VIDEO') {
        el.querySelectorAll('source[data-src]').forEach(source => {
          source.src = source.dataset.src;
          delete source.dataset.src;
        });
        el.load();
        el.play().catch(() => {});
      } else {
        if (el.dataset.srcset) {
          el.srcset = el.dataset.srcset;
          delete el.dataset.srcset;
        }
        if (el.dataset.src) {
          el.src = el.dataset.src;
          delete el.dataset.src;
        }
        card.removeEventListener('mouseenter', onMouseEnter);
      }
    }

    function onMouseLeave() {
      if (el.tagName === 'VIDEO') {
        el.pause();
        el.currentTime = 0;
      }
    }

    card.addEventListener('mouseenter', onMouseEnter);
    card.addEventListener('mouseleave', onMouseLeave);
  });
}

callOnDocReady(initRolloverImages);

function initLazyImages() {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const img = entry.target;

      if (img.closest('.header__nav') && window.innerWidth < 1024) return;
      if (img.dataset.srcset) { img.srcset = img.dataset.srcset; delete img.dataset.srcset; }
      if (img.dataset.src) { img.src = img.dataset.src; delete img.dataset.src; }

      observer.unobserve(img);
    });
  }, { rootMargin: '200px' });

  document.querySelectorAll('img.js-lazy').forEach(img => observer.observe(img));
}

callOnDocReady(initLazyImages);

function initLazyVideos() {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const video = entry.target;

      video.querySelectorAll('source[data-src]').forEach(source => {
        source.src = source.dataset.src;
        delete source.dataset.src;
      });
      video.load();
      video.play().catch(() => {});

      observer.unobserve(video);
    });
  }, { rootMargin: '200px' });

  document.querySelectorAll('video.js-lazy').forEach(video => observer.observe(video));
}

callOnDocReady(initLazyVideos);

function initPlpVideoObservers() {
  if (!('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const wrapper = entry.target;
        const videos = wrapper.querySelectorAll('.js-card-video-primary');
        if (!videos.length) return;

        videos.forEach((video) => {
          if (entry.isIntersecting) {
            video.querySelectorAll('source[data-src]').forEach(source => {
              source.src = source.dataset.src;
              delete source.dataset.src;
            });
            if (video.querySelector('source[src]')) {
              video.load();
            }
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      });
    },
    { threshold: 0.3 }
  );

  document.querySelectorAll('.card-wrapper').forEach((card) => {
    observer.observe(card);
  });
}

callOnDocReady(initPlpVideoObservers);
